import { useEffect, useState } from "react";
import api from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
// Logos stored at /partners/* are frontend static assets — leave as-is.
// Logos stored at /uploads/* come from the backend — prepend the backend URL.
const resolveUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/uploads/")) return `${BACKEND}${u}`;
  return u; // static frontend path
};

// Continuous-marquee partners strip — uses CSS animation and duplicates the list
// so the loop is seamless. Logos remain clickable.
export default function PartnersMarquee() {
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    let mounted = true;
    api.get("/partners")
      .then((r) => {
        if (!mounted) return;
        const items = (r.data?.items || r.data || [])
          .filter((p) => p.logo_url || p.name)
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        setPartners(items);
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  if (partners.length === 0) return null;
  // Duplicate the list for a seamless infinite loop
  const loop = [...partners, ...partners];

  return (
    <div
      data-testid="partners-marquee"
      className="border-t border-b py-8 overflow-hidden"
      style={{ borderColor: "var(--border)", background: "var(--parchment)" }}
    >
      <div className="relative" style={{ maskImage: "linear-gradient(to right, transparent, black 8%, black 92%, transparent)" }}>
        <div className="flex items-center gap-12 partners-marquee-track" style={{ width: "max-content" }}>
          {loop.map((p, idx) => {
            const href = p.url || "#";
            const isExternal = href.startsWith("http");
            const inner = (
              <>
                {p.logo_url ? (
                  <img
                    src={resolveUrl(p.logo_url)}
                    alt={p.name}
                    title={p.name}
                    className="object-contain transition-opacity"
                    style={{ height: "60px", maxWidth: "180px", filter: "grayscale(20%)", opacity: 0.85 }}
                  />
                ) : (
                  <span className="font-display text-lg" style={{ color: "var(--ink-2)" }}>{p.name}</span>
                )}
              </>
            );
            return isExternal ? (
              <a
                key={`${p.id}-${idx}`}
                href={href}
                target="_blank"
                rel="noreferrer noopener"
                title={p.name}
                data-testid={`partner-link-${p.id}`}
                className="shrink-0 hover:opacity-100"
                onMouseEnter={(e) => { const img = e.currentTarget.querySelector("img"); if (img) { img.style.filter = "none"; img.style.opacity = "1"; } }}
                onMouseLeave={(e) => { const img = e.currentTarget.querySelector("img"); if (img) { img.style.filter = "grayscale(20%)"; img.style.opacity = "0.85"; } }}
              >
                {inner}
              </a>
            ) : (
              <div key={`${p.id}-${idx}`} className="shrink-0">{inner}</div>
            );
          })}
        </div>
      </div>

      <style>{`
        @keyframes partners-marquee-scroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        .partners-marquee-track {
          animation: partners-marquee-scroll 40s linear infinite;
        }
        .partners-marquee-track:hover { animation-play-state: paused; }
      `}</style>
    </div>
  );
}
